import { z } from 'zod';
import { StaffLevel } from './admin-users.js';
/**
 * Giá thuê và hệ số tốc độ của thành viên trên **một dự án** —
 * docs/plan-project-rate-speed.md.
 *
 * ## Đây không phải bảng lương
 *
 * `cost_rate` với `subject_type = user` là **lương**: dữ liệu cấp tổ chức, chỉ
 * `ORG_ADMIN` nhập và đọc. Hai bảng ở đây là **giá thuê** — dự án trả bao nhiêu
 * cho mỗi giờ của người này — do PM thoả thuận và PM chịu trách nhiệm. Hai con
 * số thường liên quan nhưng không bằng nhau, và người xem được cái này không
 * đương nhiên xem được cái kia.
 *
 * Vì một **tổng** cũng là một kênh rò (đặt giá cho một người rồi so tổng
 * trước/sau là suy ngược ra được), chuỗi rơi đơn giá bỏ hẳn nhánh `cost_rate.user`
 * với người gọi không có `budget:manage` không điều kiện. Xem `BudgetEstimateService`.
 */
/**
 * Mức khó của việc — **suy từ số giờ**, không phải một cột trên `task`.
 *
 * Bản Zod của `TaskDifficulty` ở `@apm/estimate`. Hai bản vì hai gói: ở đây là
 * **hình dạng** dữ liệu qua dây, ở đó là **phép tính** — cùng ranh giới đã có
 * sẵn giữa `Confidence` của hai bên.
 */
export const TaskDifficulty = z.enum(['simple', 'standard', 'hard']);
/**
 * Khoảng cho phép của hệ số tốc độ.
 *
 * `1.00` là mức của một người middle chuẩn; **lớn hơn 1 là chậm hơn**, nhỏ hơn 1
 * là nhanh hơn — cùng chiều `velocity_sample.ratio` (`worked/estimate`), vì hai
 * con số này nằm cạnh nhau trên màn hình.
 */
export const SPEED_FACTOR_MIN = 0.25;
export const SPEED_FACTOR_MAX = 4;
/**
 * Giá thuê **mặc định theo bậc**, cấp tổ chức — §11.
 *
 * Quản trị viên tổ chức đặt bảng này một lần; PM của từng dự án chọn dùng nó
 * hoặc gõ một giá riêng. Đó là lý do nó tồn tại: không có nó thì mỗi PM phải tự
 * gõ giá cho từng người ở từng dự án, và mười dự án sẽ có mười bảng giá lệch nhau.
 */
export const LevelRateView = z.object({
    level: StaffLevel,
    hourlyRate: z.number().positive(),
    currency: z.string().length(3),
    effectiveFrom: z.string(),
    note: z.string().nullable(),
});
export const SetLevelRateInput = z.object({
    level: StaffLevel,
    hourlyRate: z.number().positive().max(100_000_000),
    currency: z.string().length(3).default('VND'),
    effectiveFrom: z.iso.date(),
    note: z.string().max(200).nullable().optional(),
});
export const ProjectMemberRateView = z.object({
    userId: z.uuid(),
    hourlyRate: z.number().positive(),
    currency: z.string().length(3),
    effectiveFrom: z.string(),
    note: z.string().nullable(),
});
export const ProjectMemberSpeedView = z.object({
    bucket: TaskDifficulty,
    factor: z.number().positive(),
    note: z.string().nullable(),
});
/**
 * Tốc độ **đo được** từ việc đã xong, để PM chỉnh từ một mốc có căn cứ.
 *
 * ⚠️ Con số này **không** vào phép tính tiền. Nó là dữ liệu quan sát; để nó tự
 * chảy vào tổng chi phí là đổi ngân sách của mọi dự án mà không ai bấm nút nào.
 * Chỉ hệ số PM đặt tay mới tính tiền — xem `RateLookup.speedByUser`.
 *
 * `null` khi chưa đủ mẫu. "Chưa biết" phải khác "đúng mức chuẩn": điền đại
 * `1.00` vào một ô trống là bịa ra một kết luận về một con người.
 */
export const LearnedSpeedView = z.object({
    bucket: TaskDifficulty,
    medianRatio: z.number().positive().nullable(),
    samples: z.number().int().nonnegative(),
});
/**
 * Giá đang áp cho một người, và **vì sao là con số ấy** — §11.
 *
 * `custom` PM tự gõ · `level` mặc định theo bậc · `none` chưa có cả hai, phép
 * tính rơi tiếp xuống chức vụ rồi kỹ năng.
 *
 * Trường này tồn tại để giao diện nói được "đang dùng mặc định của Senior 1"
 * thay vì chỉ hiện một con số. Một con số không nói nó từ đâu ra thì người đọc
 * không biết sửa nó ở chỗ nào — bảng giá của tổ chức hay ô ngay trước mặt.
 */
export const RateSource = z.enum(['custom', 'level', 'none']);
/** Một dòng trong bảng nhân sự dự án: người, giá thuê, hệ số, và số đã đo được. */
export const ProjectStaffingRow = z.object({
    userId: z.uuid(),
    displayName: z.string(),
    roleName: z.string().nullable(),
    /** Bậc trong tổ chức; `null` = chưa xếp bậc. Quản trị viên tổ chức đặt, PM chỉ đọc. */
    level: StaffLevel.nullable(),
    /** Giá riêng PM gõ cho người này ở dự án này; `null` = đang dùng mặc định. */
    rate: ProjectMemberRateView.nullable(),
    /** Mặc định theo bậc, để hiện cạnh ô nhập; `null` khi chưa xếp bậc hoặc bậc chưa có giá. */
    levelRate: z.number().positive().nullable(),
    /** Con số **thật sự** đang vào phép tính, và nguồn của nó. */
    effectiveRate: z.number().positive().nullable(),
    rateSource: RateSource,
    speeds: z.array(ProjectMemberSpeedView),
    learned: z.array(LearnedSpeedView),
});
export const ProjectStaffingView = z.object({
    projectId: z.uuid(),
    rows: z.array(ProjectStaffingRow),
    currency: z.string().length(3),
});
export const SetProjectMemberRateInput = z.object({
    userId: z.uuid(),
    hourlyRate: z.number().positive().max(100_000_000),
    currency: z.string().length(3).default('VND'),
    /** Ngày bắt đầu hiệu lực. Dòng đang hiệu lực bị **đóng** tại ngày này, không bị sửa. */
    effectiveFrom: z.iso.date(),
    note: z.string().max(200).nullable().optional(),
});
export const SetProjectMemberSpeedInput = z.object({
    userId: z.uuid(),
    bucket: TaskDifficulty,
    factor: z.number().min(SPEED_FACTOR_MIN).max(SPEED_FACTOR_MAX),
    effectiveFrom: z.iso.date(),
    note: z.string().max(200).nullable().optional(),
});
//# sourceMappingURL=project-rate.js.map